import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { PhoneCall, Plus, Camera, X } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MAX_PHOTOS = 8;
const MAX_BYTES = 8 * 1024 * 1024;

const ServiceRequest = () => {
  const [requests, setRequests] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [serviceType, setServiceType] = useState('');
  const [description, setDescription] = useState('');
  const [preferredDate, setPreferredDate] = useState('');
  const [customerNotes, setCustomerNotes] = useState('');
  const [photos, setPhotos] = useState([]); // [{ file, preview }]
  const [loading, setLoading] = useState(false);

  useEffect(() => { fetchRequests(); }, []);

  const fetchRequests = async () => {
    try {
      const { data } = await axios.get(`${API}/service-requests`, { withCredentials: true });
      setRequests(data || []);
    } catch (error) {
      console.error('Error fetching requests:', error);
    }
  };

  const handlePhotos = (e) => {
    const files = Array.from(e.target.files || []);
    const next = [...photos];
    for (const f of files) {
      if (next.length >= MAX_PHOTOS) { toast.error(`Max ${MAX_PHOTOS} photos`); break; }
      if (!f.type.startsWith('image/')) { toast.error(`${f.name} is not an image`); continue; }
      if (f.size > MAX_BYTES) { toast.error(`${f.name} exceeds 8MB`); continue; }
      next.push({ _uid: (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `p_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`), file: f, preview: URL.createObjectURL(f) });
    }
    setPhotos(next);
    e.target.value = '';
  };

  const removePhoto = (idx) => {
    setPhotos((prev) => prev.filter((_, i) => i !== idx));
  };

  const resetForm = () => {
    setShowForm(false);
    setServiceType('');
    setDescription('');
    setPreferredDate('');
    setCustomerNotes('');
    photos.forEach((p) => URL.revokeObjectURL(p.preview));
    setPhotos([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('service_type', serviceType);
      fd.append('description', description);
      fd.append('preferred_date', preferredDate);
      fd.append('customer_notes', customerNotes || '');
      photos.forEach((p) => fd.append('photos', p.file, p.file.name));
      await axios.post(`${API}/service-requests/with-photos`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Service request submitted${photos.length ? ` with ${photos.length} photo(s)` : ''}`);
      resetForm();
      await fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    }
    setLoading(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-500 bg-green-500/10';
      case 'scheduled': return 'text-blue-500 bg-blue-500/10';
      case 'cancelled': return 'text-red-500 bg-red-500/10';
      default: return 'text-[#D4AF37] bg-[#D4AF37]/10';
    }
  };

  const photoUrl = (req, fileId) =>
    `${API}/service-requests/${req.id || req._id || ''}/photos/${fileId}`;

  return (
    <div data-testid="service-request-page" className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-['Outfit'] text-3xl font-bold text-white mb-2">Service Requests</h2>
          <p className="text-[#A3A3A3]">Request plumbing services. Add photos so we can prepare the right parts.</p>
        </div>
        <Button
          data-testid="new-service-request-button"
          onClick={() => setShowForm(!showForm)}
          className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Request
        </Button>
      </div>

      {showForm && (
        <div className="bg-[#141414] border border-[#262626] rounded-md p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="service-type" className="text-white">Service Type</Label>
              <Select value={serviceType} onValueChange={setServiceType} required>
                <SelectTrigger data-testid="service-type-select" id="service-type" className="bg-[#0A0A0A] border-[#262626] text-white">
                  <SelectValue placeholder="Select service type" />
                </SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#262626]">
                  <SelectItem value="Plumbing Problem" className="text-white">Plumbing Problem</SelectItem>
                  <SelectItem value="Water Heater" className="text-white">Water Heater</SelectItem>
                  <SelectItem value="Drain Issue" className="text-white">Drain Issue</SelectItem>
                  <SelectItem value="Emergency Plumbing" className="text-white">Emergency Plumbing</SelectItem>
                  <SelectItem value="Estimate Request" className="text-white">Estimate Request</SelectItem>
                  <SelectItem value="Maintenance Visit" className="text-white">Maintenance Visit</SelectItem>
                  <SelectItem value="Membership Service" className="text-white">Membership Service</SelectItem>
                  <SelectItem value="Leak or Water Damage" className="text-white">Leak or Water Damage</SelectItem>
                  <SelectItem value="Sump Pump Issue" className="text-white">Sump Pump Issue</SelectItem>
                  <SelectItem value="Sewer Backup" className="text-white">Sewer Backup</SelectItem>
                  <SelectItem value="Other" className="text-white">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="description" className="text-white">Description</Label>
              <Textarea
                data-testid="service-description"
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                required
                rows={4}
                placeholder="Describe the issue or service needed..."
                className="bg-[#0A0A0A] border-[#262626] text-white"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="customer-notes" className="text-white">Additional Notes <span className="text-[#525252] text-xs font-normal">(optional)</span></Label>
              <Textarea
                data-testid="service-customer-notes"
                id="customer-notes"
                value={customerNotes}
                onChange={(e) => setCustomerNotes(e.target.value)}
                rows={2}
                placeholder="Gate code, pets, parking notes, preferred entry, etc."
                className="bg-[#0A0A0A] border-[#262626] text-white"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="preferred-date" className="text-white">Preferred Date</Label>
              <Input
                data-testid="preferred-date"
                id="preferred-date"
                type="date"
                value={preferredDate}
                onChange={(e) => setPreferredDate(e.target.value)}
                required
                className="bg-[#0A0A0A] border-[#262626] text-white"
              />
            </div>

            {/* Photo uploader */}
            <div className="space-y-2">
              <Label className="text-white">Photos <span className="text-[#525252] text-xs font-normal">(up to {MAX_PHOTOS}, ≤8MB each)</span></Label>
              <label
                data-testid="service-photo-uploader"
                htmlFor="photo-input"
                className="flex items-center gap-2 cursor-pointer border border-dashed border-[#262626] hover:border-[#D4AF37] rounded-md px-4 py-3 text-[#A3A3A3] hover:text-white transition-colors"
              >
                <Camera className="w-4 h-4" />
                <span className="text-sm">Tap to add photos of the issue</span>
              </label>
              <input
                id="photo-input"
                data-testid="service-photo-input"
                type="file"
                accept="image/*"
                multiple
                onChange={handlePhotos}
                className="hidden"
              />
              {photos.length > 0 && (
                <div data-testid="service-photo-previews" className="grid grid-cols-3 sm:grid-cols-4 gap-3 mt-3">
                  {photos.map((p, idx) => (
                    <div key={p._uid} className="relative group">
                      <img src={p.preview} alt="" className="w-full h-24 object-cover rounded-md border border-[#262626]" />
                      <button
                        type="button"
                        onClick={() => removePhoto(idx)}
                        data-testid={`remove-photo-${idx}`}
                        className="absolute top-1 right-1 bg-black/70 text-white rounded-full p-1 hover:bg-red-600"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex gap-2">
              <Button
                data-testid="service-request-submit-button"
                type="submit"
                disabled={loading}
                className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]"
              >
                {loading ? 'Submitting…' : 'Submit Request'}
              </Button>
              <Button
                type="button"
                onClick={resetForm}
                variant="outline"
                className="border-[#262626] text-white hover:bg-[#262626]"
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}

      <div className="grid gap-4">
        {(!requests || requests.length === 0) ? (
          <div className="bg-[#141414] border border-[#262626] rounded-md p-8 text-center text-[#A3A3A3]">
            No service requests yet
          </div>
        ) : (
          requests.map((request, idx) => (
            <div key={request.id || idx} className="bg-[#141414] border border-[#262626] rounded-md p-6 hover:border-[#D4AF37] transition-all duration-200">
              <div className="flex items-start justify-between gap-4">
                <div className="flex gap-4 flex-1 min-w-0">
                  <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-md flex items-center justify-center shrink-0">
                    <PhoneCall className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-medium text-lg mb-1">{request.service_type}</h3>
                    <p className="text-[#A3A3A3] text-sm mb-2">{request.description}</p>
                    {request.customer_notes && (
                      <p className="text-[#A3A3A3] text-xs italic mb-2">Notes: {request.customer_notes}</p>
                    )}
                    <p className="text-[#A3A3A3] text-xs">Preferred: {request.preferred_date}</p>
                    {Array.isArray(request.photos) && request.photos.length > 0 && (
                      <div className="mt-3 flex gap-2 flex-wrap">
                        {request.photos.map((ph) => (
                          <a key={ph.file_id} href={photoUrl(request, ph.file_id)} target="_blank" rel="noreferrer">
                            <img
                              src={photoUrl(request, ph.file_id)}
                              alt={ph.filename}
                              className="w-16 h-16 object-cover rounded-md border border-[#262626] hover:border-[#D4AF37]"
                            />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className={`px-3 py-1 rounded-md text-xs font-medium whitespace-nowrap ${getStatusColor(request.status)}`}>
                  {(request.status || 'pending').toUpperCase()}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ServiceRequest;
